/**
 * scanSounds — tiny WebAudio-driven sound cues for the scanner UI.
 *
 * We avoid bundling MP3s (bloats the app + browser autoplay policy pain)
 * and instead synthesise short tones via WebAudio OscillatorNodes. The
 * audio context is created lazily on the first user gesture (mount of
 * the scanner counts, since the operator clicks or types on the page
 * before their first scan).
 *
 * Verdict → sound
 * ---------------
 *   green   two short high beeps (welcome chime)
 *   amber   one short mid beep + one lower beep (warning)
 *   red     three short low beeps (error)
 *   vip     four-note ascending fanfare — for GREEN scans of vip delegates
 */
let _ctx = null;

function _getCtx() {
  if (_ctx && _ctx.state !== 'closed') return _ctx;
  const AC = typeof window !== 'undefined'
    ? (window.AudioContext || window.webkitAudioContext) : null;
  if (!AC) return null;
  _ctx = new AC();
  return _ctx;
}

function _beep(freq, durSec = 0.09, startAt = 0, gainLevel = 0.18) {
  const ctx = _getCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  const t0 = ctx.currentTime + startAt;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(gainLevel, t0 + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + durSec);
  osc.connect(gain).connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + durSec + 0.02);
}

export function playScanSound(verdict, opts = {}) {
  const { vip = false } = opts;
  try {
    if (verdict === 'green') {
      if (vip) {
        // ascending fanfare
        _beep(523, 0.11, 0);       // C5
        _beep(659, 0.11, 0.11);    // E5
        _beep(784, 0.11, 0.22);    // G5
        _beep(1046, 0.22, 0.33, 0.22); // C6, longer
      } else {
        _beep(880, 0.08, 0);
        _beep(1175, 0.08, 0.08);
      }
    } else if (verdict === 'amber') {
      _beep(660, 0.10, 0);
      _beep(440, 0.14, 0.10);
    } else if (verdict === 'red') {
      _beep(220, 0.10, 0);
      _beep(220, 0.10, 0.14);
      _beep(196, 0.18, 0.28);
    }
  } catch { /* audio failure never blocks a scan */ }
}
